import React, { useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";

const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITEKEY = process.env.REACT_APP_TURNSTILE_SITEKEY || "1x00000000000000000000AA"; // Cloudflare test key (always passes) as safe default

export default function AuthModal({ open, onOpenChange, initialMode = "login", onSuccess }) {
  const [mode, setMode] = useState(initialMode);  // "login" | "signup" | "forgot" | "forgot-sent"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [breached, setBreached] = useState(false);
  const turnstileRef = useRef(null);
  const { login, signup } = useAuth();

  // Sync `mode` from parent ONLY when initialMode itself changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setMode(initialMode); }, [initialMode]);
  // Clear form fields every time the modal is (re)opened so a stale password
  // from a cancelled signup attempt can't sneak into the next login.
  React.useEffect(() => {
    if (open) {
      setEmail("");
      setPassword("");
      setName("");
      setTurnstileToken("");
      setBreached(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back.");
        onOpenChange(false);
        onSuccess && onSuccess();
      } else if (mode === "signup") {
        if (breached) { toast.error("This password appears in known breaches — pick another."); setBusy(false); return; }
        await signup(email, password, name || email.split("@")[0]);
        toast.success("Account created. Let's mix.");
        onOpenChange(false);
        onSuccess && onSuccess();
      } else if (mode === "forgot") {
        if (!turnstileToken) {
          toast.error("Please complete the security check first.");
          setBusy(false);
          return;
        }
        await axios.post(`${API}/auth/forgot-password`, {
          email,
          turnstile_token: turnstileToken,
        });
        setMode("forgot-sent");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "login" ? "Log in to NeuralDeck"
    : mode === "signup" ? "Create your NeuralDeck account"
    : mode === "forgot" ? "Reset your password"
    : "Check your inbox";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#121620] border-[#2A354B] text-white sm:max-w-md"
        data-testid="auth-modal"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        aria-describedby={undefined}
      >
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl">{title}</DialogTitle>
        </DialogHeader>

        {mode === "forgot-sent" ? (
          <div className="space-y-4 pt-2" data-testid="auth-forgot-sent">
            <p className="text-sm text-[#F8FAFC]">
              If an account exists for <span className="font-mono text-[#00F0FF]">{email}</span>, we've emailed a reset link. It expires in 1 hour.
            </p>
            <p className="text-xs text-[#94A3B8]">Check spam / junk if it doesn't land in a minute.</p>
            <Button data-testid="auth-back-to-login" onClick={() => { setMode("login"); }}
              className="w-full bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
              Back to log in
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            {mode === "signup" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Display Name</Label>
                <Input data-testid="auth-name-input" value={name} onChange={e => setName(e.target.value)} className="bg-[#0B0E14] border-[#2A354B] text-white"/>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Email</Label>
              <Input data-testid="auth-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="bg-[#0B0E14] border-[#2A354B] text-white"/>
            </div>
            {mode !== "forgot" && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-widest text-[#94A3B8]">Password</Label>
                  {mode === "login" && (
                    <button data-testid="auth-forgot-link" type="button"
                      onClick={() => { setPassword(""); setMode("forgot"); }}
                      className="text-[10px] uppercase tracking-widest text-[#00F0FF] hover:text-white font-bold">
                      Forgot?
                    </button>
                  )}
                </div>
                <PasswordInput testId="auth-password-input" required minLength={6} value={password} onChange={e => setPassword(e.target.value)} />
                {mode === "signup" && <PasswordStrengthMeter password={password} testId="auth-pw-strength" onBreachChange={setBreached}/>}
              </div>
            )}
            {mode === "forgot" && (
              <div data-testid="auth-turnstile-wrap" className="flex justify-center py-2">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITEKEY}
                  onSuccess={setTurnstileToken}
                  onError={() => setTurnstileToken("")}
                  onExpire={() => setTurnstileToken("")}
                  options={{ theme: "dark", size: "flexible" }}
                />
              </div>
            )}
            <Button data-testid="auth-submit-button" type="submit" disabled={busy || (mode === "forgot" && !turnstileToken) || (mode === "signup" && breached)}
              className="w-full bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
              {busy ? "…" :
                mode === "login" ? "Log in" :
                mode === "signup" ? "Create account" :
                "Send reset link"}
            </Button>
            <div className="text-center text-xs text-[#94A3B8]">
              {mode === "forgot" ? (
                <button data-testid="auth-back-to-login-link" type="button" className="text-[#00F0FF] font-bold uppercase tracking-widest" onClick={() => setMode("login")}>
                  ← Back to log in
                </button>
              ) : (
                <>
                  {mode === "login" ? "New here?" : "Have an account?"}{" "}
                  <button data-testid="auth-toggle-mode" type="button" className="text-[#00F0FF] font-bold uppercase tracking-widest"
                    onClick={() => setMode(mode === "login" ? "signup" : "login")}>
                    {mode === "login" ? "Sign up" : "Log in"}
                  </button>
                </>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
